import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { createFile } from "@/lib/api";

export default function PasteDialog({ open, onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!content.trim()) {
      toast.error("Paste some XML first");
      return;
    }
    setBusy(true);
    try {
      await createFile(name.trim() || "Pasted.xml", content);
      toast.success("XML parsed & stored");
      setName("");
      setContent("");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid XML");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#121212] border-zinc-800 rounded-sm max-w-2xl"
        data-testid="paste-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Paste ADAC XML
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-zinc-500">
            Paste raw ADAC XML; it will be parsed, validated and stored.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-mono text-zinc-400">FILE NAME</Label>
            <Input
              data-testid="paste-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="As_Constructed_R2.xml"
              className="rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-mono text-zinc-400">XML CONTENT</Label>
            <Textarea
              data-testid="paste-content-input"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="<ADAC>…</ADAC>"
              className="rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs h-64 resize-none"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-sm text-zinc-400"
          >
            Cancel
          </Button>
          <Button
            data-testid="paste-save-btn"
            onClick={submit}
            disabled={busy}
            className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white"
          >
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Parse & Store
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
